/**
 * Fabrication Dashboard API (pih-server)
 * @see /pih-server/docs/FABRICATION_DASHBOARD_CURL.md
 */
import { put, call, all, takeLatest } from 'redux-saga/effects';
import get from 'lodash/get';
import axios from 'axios';
import { notification } from 'antd';
import axiosConfig from '../sparesDashboard/axiosConfig';
import { fabricationApiBasePath } from '../../utils/fabricationApiMapper';
import {
  fetchFabricationJobsSuccess,
  fetchFabricationJobsFailure,
  createFabricationJobSuccess,
  createFabricationJobFailure,
  updateFabricationJobSuccess,
  updateFabricationJobFailure,
  deleteFabricationJobSuccess,
  deleteFabricationJobFailure,
} from './action';
import {
  FETCH_FABRICATION_JOBS,
  CREATE_FABRICATION_JOB,
  UPDATE_FABRICATION_JOB,
  DELETE_FABRICATION_JOB,
} from './types';

const apiErrorMessage = (error) =>
  get(error, 'error', get(error, 'message', 'Something went wrong. Please try again.'));

function* refetchJobs(projectId) {
  if (projectId) {
    yield call(fetchFabricationJobsWorker, { projectId });
  }
}

function* fetchFabricationJobsWorker({ projectId }) {
  try {
    const res = yield axiosConfig.get(
      `${fabricationApiBasePath(projectId)}-records`
    );
    const response = get(res, ['data', 'data'], []);
    if (res.status === 200) {
      yield put(fetchFabricationJobsSuccess({ response }));
    } else {
      yield put(fetchFabricationJobsFailure());
      notification.error({ message: 'Something went wrong. Please try again.' });
    }
  } catch (error) {
    yield put(fetchFabricationJobsFailure());
    notification.destroy();
    notification.error({
      message: 'A problem occurred while fetching fabrication jobs.',
      description: String(apiErrorMessage(error)),
    });
  }
}

function* createFabricationJobWorker({ payload, selectedProject }) {
  const projectId = selectedProject?.id;
  try {
    const res = yield axiosConfig.post(
      `${fabricationApiBasePath(projectId)}-record`,
      payload
    );
    if (res.status === 200 || res.status === 201) {
      yield put(createFabricationJobSuccess({ response: get(res, ['data', 'data'], {}) }));
      yield refetchJobs(projectId);
      notification.success({ message: 'Fabrication job added successfully.' });
    } else {
      yield put(createFabricationJobFailure());
      notification.error({ message: 'Something went wrong.' });
    }
  } catch (error) {
    yield put(createFabricationJobFailure());
    notification.destroy();
    notification.error({
      message: 'A problem occurred while adding the fabrication job.',
      description: String(apiErrorMessage(error)),
    });
  }
}

function* updateFabricationJobWorker({ payload, selectedProject }) {
  const projectId = selectedProject?.id;
  const id = payload?.id;
  const { id: _omit, ...body } = payload || {};
  try {
    const res = yield axiosConfig.put(
      `${fabricationApiBasePath(projectId)}-record/${id}`,
      body
    );
    if (res.status === 200) {
      yield put(updateFabricationJobSuccess({ response: get(res, ['data', 'data'], {}) }));
      yield refetchJobs(projectId);
      notification.success({ message: 'Fabrication job updated successfully.' });
    } else {
      yield put(updateFabricationJobFailure());
      notification.error({ message: 'Something went wrong. Please try again later.' });
    }
  } catch (error) {
    yield put(updateFabricationJobFailure());
    notification.destroy();
    notification.error({
      message: 'Fabrication job update failed.',
      description: String(apiErrorMessage(error)),
    });
  }
}

function* deleteFabricationJobWorker({ id, selectedProject }) {
  const projectId = selectedProject?.id;
  try {
    const res = yield axiosConfig.delete(
      `${fabricationApiBasePath(projectId)}-record/${id}`
    );
    if (res.status === 200) {
      yield put(deleteFabricationJobSuccess({ id }));
      yield refetchJobs(projectId);
      notification.success({ message: 'Fabrication job deleted successfully.' });
    } else {
      yield put(deleteFabricationJobFailure());
      notification.error({ message: 'Something went wrong.' });
    }
  } catch (error) {
    yield put(deleteFabricationJobFailure());
    notification.destroy();
    notification.error({
      message: 'A problem occurred while deleting the fabrication job.',
      description: String(apiErrorMessage(error)),
    });
  }
}

function* fabricationJobsWatcher() {
  yield takeLatest(FETCH_FABRICATION_JOBS, fetchFabricationJobsWorker);
  yield takeLatest(CREATE_FABRICATION_JOB, createFabricationJobWorker);
  yield takeLatest(UPDATE_FABRICATION_JOB, updateFabricationJobWorker);
  yield takeLatest(DELETE_FABRICATION_JOB, deleteFabricationJobWorker);
}

function* fabricationJobsSagas() {
  yield all([call(fabricationJobsWatcher)]);
}

export default fabricationJobsSagas;

/** GET presigned URL → PUT S3 → POST process upload */
export const onUploadFabricationExcel = async (file, projectId) => {
  try {
    if (!file || !projectId) return false;
    const resFile = await axiosConfig.get(
      `${fabricationApiBasePath(projectId)}-records/upload/presigned-url?fileName=${encodeURIComponent(file.name)}&contentType=${encodeURIComponent(file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')}`
    );
    const responseFile = get(resFile, ['data', 'data'], {});
    if (responseFile?.presignedUrl) {
      const presignedResponse = await axios.put(responseFile.presignedUrl, file);
      if (presignedResponse.status !== 200) {
        notification.error({ message: 'File upload failed. Please try again.' });
        return false;
      }
      const res = await axiosConfig.post(
        `${fabricationApiBasePath(projectId)}-records/upload`,
        { fileKey: responseFile.fileKey }
      );
      const uploadData = get(res, ['data', 'data'], {});
      if (res.status === 200) {
        const failed = uploadData.failedRows ?? 0;
        if (failed > 0) {
          notification.warning({
            message: 'Excel uploaded with errors',
            description: `${uploadData.processedRows ?? 0} row(s) saved, ${failed} failed.`,
          });
        } else {
          notification.success({
            message: 'Excel uploaded successfully.',
            description: `${uploadData.processedRows ?? uploadData.totalRows ?? 0} row(s) processed.`,
          });
        }
        return true;
      }
    }
    notification.error({ message: 'File upload failed. Please try again.' });
    return false;
  } catch (error) {
    notification.destroy();
    notification.error({
      message: 'A problem occurred while uploading the file.',
      description: String(apiErrorMessage(error)),
    });
    return false;
  }
};

/** Server-generated Excel for project */
export const downloadFabricationJobsFromApi = async (projectId) => {
  try {
    const res = await axiosConfig.get(
      `${fabricationApiBasePath(projectId)}-records/download`
    );
    const fileUrl =
      get(res, ['data', 'fileUrl']) ||
      get(res, ['data', 'data', 'fileUrl']);
    const fileName =
      get(res, ['data', 'fileName']) ||
      get(res, ['data', 'data', 'fileName']) ||
      `fabrication-dashboard-project-${projectId}.xlsx`;
    if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
      return true;
    }
    notification.error({ message: 'Download failed. No file URL returned.' });
    return false;
  } catch (error) {
    notification.destroy();
    notification.error({
      message: 'A problem occurred while downloading the file.',
      description: String(apiErrorMessage(error)),
    });
    return false;
  }
};
